import { useCallback } from 'react';
import { Link } from 'react-router-dom-v5-compat';

import { Text, TextType } from 'ui-components/components/text';

import { BASE_ORDERS_PATH } from '../../../../routes/constants';
import { ADVANCED_SEARCH_ID } from '../../../../pages/orders-page/orders-page-view-constants';
import {
  STATUS_VALUE_MAP,
  STRING_FILTER_MAPPINGS,
  FILTER_IDS,
  FILTER_OPERATORS,
  SHIPPY_FILTERS_STATE_KEY,
} from '../../../../advanced-search/orders/constants/shippy-search-constants';
import type { ShippyFilter } from '../../../../advanced-search/orders/hooks/use-shippy-search-filters';
import { useHandlerTracking } from '../../hooks/use-handler-tracking';
import { messages } from '../../messages';
import { CAPABILITY_IDS } from '../capabilities-registry';
import style from '../../style.module.less';

type OrderSearchParameters = {
  orderStatus?: string[];
  storeGuids?: string[];
  orderNumber?: string;
  recipientName?: string;
  email?: string;
  itemName?: string;
  itemSku?: string;
  itemOption?: string;
  orderDateFrom?: string;
  orderDateTo?: string;
};

type DateRange = {
  from?: string;
  to?: string;
};

const resolveDateRange = (parameters: OrderSearchParameters): DateRange | null => {
  const { orderDateFrom, orderDateTo } = parameters;

  if (!orderDateFrom && !orderDateTo) {
    return null;
  }

  const dateRange: DateRange = {};
  if (orderDateFrom) {
    dateRange.from = orderDateFrom;
  }
  if (orderDateTo) {
    dateRange.to = orderDateTo;
  }
  return dateRange;
};

export const buildAdvancedSearchFilters = (parameters: OrderSearchParameters): ShippyFilter[] => {
  const filters: ShippyFilter[] = [];

  STRING_FILTER_MAPPINGS.forEach(({ param, filterId }) => {
    const value = parameters[param as keyof OrderSearchParameters];
    if (value) {
      filters.push({
        id: filterId,
        value: [value],
        operator: FILTER_OPERATORS.contains,
      });
    }
  });

  if (parameters.orderStatus) {
    const mappedStatuses = parameters.orderStatus.map((s) => STATUS_VALUE_MAP[s as keyof typeof STATUS_VALUE_MAP]).filter(Boolean);
    if (mappedStatuses.length > 0) {
      filters.push({
        id: FILTER_IDS.orderStatus,
        value: mappedStatuses,
        operator: FILTER_OPERATORS.is,
      });
    }
  }

  if (parameters.storeGuids) {
    const validGuids = parameters.storeGuids.filter(Boolean);
    if (validGuids.length > 0) {
      filters.push({
        id: FILTER_IDS.storeGuid,
        value: validGuids,
        operator: FILTER_OPERATORS.is,
      });
    }
  }

  const dateRange = resolveDateRange(parameters);
  if (dateRange) {
    filters.push({
      id: FILTER_IDS.orderDate,
      value: dateRange,
    });
  }

  return filters;
};

type OrderSearchHandlerProps = {
  parameters: OrderSearchParameters;
  conversationId: string;
  turnNumber: number;
};

export const OrderSearchHandler = ({ parameters, conversationId, turnNumber }: OrderSearchHandlerProps) => {
  const isValid = !!parameters && typeof parameters === 'object';

  const { trackAction } = useHandlerTracking({
    capabilityId: CAPABILITY_IDS.ORDER_SEARCH,
    conversationId,
    turnNumber,
    parameters: (parameters ?? {}) as Record<string, unknown>,
    isValid,
  });

  const handleClick = useCallback(() => {
    trackAction();
  }, [trackAction]);

  if (!isValid) {
    return <Text textType={TextType.Caption} value={messages.capabilityError} />;
  }

  const filters = buildAdvancedSearchFilters(parameters);
  const route = `${BASE_ORDERS_PATH}/${ADVANCED_SEARCH_ID}`;
  const routeState = filters.length > 0 ? { [SHIPPY_FILTERS_STATE_KEY]: filters } : undefined;

  return (
    <div className={style.commandLink}>
      <Link onClick={handleClick} state={routeState} to={route}>
        <Text textType={TextType.Paragraph} value={messages.commandLinkButton} />
      </Link>
    </div>
  );
};
